import React, { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { Check, Download, ExternalLink, ArrowRight, Loader2, Play, Calendar, Mail, Shield } from 'lucide-react';

const PRODUCTS = {
  foundation: {
    name: "AI Tools for Creatives — Foundation",
    type: "course",
    accessUrl: "https://www.skool.com/ip-builders-academy/classroom",
    nextSteps: [
      "Add Support@ramongovea.com to your safe sender's list in email",
      "Check your email for Skool access within the next 24 Hours or less",
      "Access all 11 Course Modules immediately",
      "Join the private Skool community",
      "Find the Templates under the Lessons and Save them",
      "Get Access on 5/1 when the Story Framework Formula book releases"
    ],
    upsell: {
      product: "vip",
      title: "Limited Time VIP Upgrade",
      description: "Upgrade to VIP for a 90-minute 1-on-1 strategy call with Ramon, pitch practice session, and direct industry connections.",
      price: 1000,
      ctaText: "Upgrade to VIP",
      scarcity: "Limited time: $1,000 discount for the first 50 students"
    }
  },
  pro: {
    name: "AI Tools for Creatives — Pro",
    type: "course",
    accessUrl: "https://www.skool.com/ip-builders-academy/classroom",
    nextSteps: [
      "Add Support@ramongovea.com to your safe sender's list in email",
      "Check your email for Skool access within the next 12 hours",
      "Access all 11 Course Modules",
      "Join the private Skool community",
      "Find the Templates under the Course Lessons and Save or Copy them",
      "Get Access on 5/1 when the Story Framework Formula book releases"
    ],
    upsell: {
      product: "vip",
      title: "Limited Time VIP Upgrade",
      description: "Upgrade to VIP for a 90-minute 1-on-1 strategy call with Ramon, pitch practice session, and direct industry connections.",
      price: 1000,
      ctaText: "Upgrade to VIP",
      scarcity: "Limited time: $1,000 discount for the first 50 students"
    }
  },
  vip: {
    name: "AI Tools for Creatives — VIP",
    type: "vip_course",
    accessUrl: "https://www.skool.com/ip-builders-academy/classroom",
    calendlyUrl: "https://calendly.com/ramon-govea/60-minute-ip-strategy-audit",
    nextSteps: [
      "Add Support@ramongovea.com to your safe sender's list in email",
      "Check your email for Skool access within the next 24 Hours or less",
      "Access all 11 Course Modules immediately",
      "Join the private Skool community",
      "Find the Templates under the Lessons and Save them",
      "Add Support@ramongovea.com to your sender's list in email",
      "Get Access on 5/1 when the Story Framework Formula book releases"
    ]
  }
};

const AIToolsSuccess = () => {
  const { productId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');
  
  const [paymentStatus, setPaymentStatus] = useState('checking');
  const [paymentData, setPaymentData] = useState(null);
  const pollAttemptsRef = useRef(0);

  const product = PRODUCTS[productId];

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    } else {
      setPaymentStatus('error');
    }
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (pollAttemptsRef.current >= maxAttempts) {
      setPaymentStatus('timeout');
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/checkout/status/${sessionId}`);
      
      if (!response.ok) {
        throw new Error('Failed to check payment status');
      }

      const data = await response.json();
      setPaymentData(data);

      if (data.payment_status === 'paid') {
        setPaymentStatus('success');
        return;
      } else if (data.status === 'expired') {
        setPaymentStatus('expired');
        return;
      }

      pollAttemptsRef.current += 1;
      if (pollAttemptsRef.current < maxAttempts) {
        setTimeout(pollPaymentStatus, pollInterval);
      } else {
        setPaymentStatus('timeout');
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      pollAttemptsRef.current += 1;
      if (pollAttemptsRef.current < maxAttempts) {
        setTimeout(pollPaymentStatus, pollInterval);
      } else {
        setPaymentStatus('error');
      }
    }
  };

  if (!product) {
    return (
      <div className="min-h-screen bg-[#08090d] text-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl mb-4" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Product not found</h1>
          <button
            onClick={() => navigate('/ai-tools-for-creatives')}
            className="text-[#E5C77A] underline"
          >
            Return to course page
          </button>
        </div>
      </div>
    );
  }

  // Loading state
  if (paymentStatus === 'checking') {
    return (
      <div className="min-h-screen bg-[#08090d] text-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 size={48} className="animate-spin text-[#E5C77A] mx-auto mb-6" />
          <h2 className="text-2xl mb-2" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Confirming Your Payment</h2>
          <p className="text-white/60">Please wait while we verify your purchase...</p>
        </div>
      </div>
    );
  }

  // Error states
  if (paymentStatus === 'error' || paymentStatus === 'expired' || paymentStatus === 'timeout') {
    return (
      <div className="min-h-screen bg-[#08090d] text-white flex items-center justify-center px-6">
        <div className="max-w-md text-center">
          <div className="w-20 h-20 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-6">
            <span className="text-4xl text-red-400">!</span>
          </div>
          <h2 className="text-2xl mb-4" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            {paymentStatus === 'expired' ? 'Payment Session Expired' : 'Payment Verification Issue'}
          </h2>
          <p className="text-white/60 mb-8">
            {paymentStatus === 'expired' 
              ? 'Your payment session has expired. Please try again.'
              : 'We had trouble verifying your payment. If you completed the purchase, please check your email for confirmation or contact support.'}
          </p>
          <div className="space-y-4">
            <button
              onClick={() => navigate(`/ai-tools-for-creatives/checkout/${productId}`)}
              className="w-full bg-gradient-to-r from-[#C6A85E] to-[#E5C77A] text-[#0F1115] py-4 font-semibold rounded-lg"
            >
              Try Again
            </button>
            <a
              href="mailto:support@ramongovea.com"
              className="block text-[#E5C77A] underline"
            >
              Contact Support
            </a>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  return (
    <div className="min-h-screen bg-[#08090d] text-white" style={{ fontFamily: "'Inter', sans-serif" }}>
      <style>{`
        .serif-heading { font-family: 'Playfair Display', Georgia, serif; }
        .glass-card {
          background: rgba(255, 255, 255, 0.03);
          border: 1px solid rgba(255, 255, 255, 0.08);
          backdrop-filter: blur(12px);
        }
        .premium-button {
          background: linear-gradient(135deg, #C6A85E, #E5C77A);
          box-shadow: 0 8px 32px rgba(198, 168, 94, 0.3);
          transition: all 250ms ease;
        }
        .premium-button:hover {
          transform: translateY(-2px);
          box-shadow: 0 12px 40px rgba(198, 168, 94, 0.4);
        }
        .secondary-button {
          border: 2px solid rgba(198, 168, 94, 0.5);
          transition: all 250ms ease;
        }
        .secondary-button:hover {
          border-color: #E5C77A;
          background: rgba(198, 168, 94, 0.1);
        }
      `}</style>

      <div className="max-w-3xl mx-auto px-6 py-16">
        {/* Success Header */}
        <div className="text-center mb-12">
          <div className="w-24 h-24 rounded-full bg-gradient-to-r from-[#C6A85E] to-[#E5C77A] flex items-center justify-center mx-auto mb-8">
            <Check size={48} className="text-[#0F1115]" />
          </div>
          <h1 className="serif-heading text-3xl md:text-4xl font-normal mb-4">Welcome to the IP Builder's Academy</h1>
          <p className="text-white/60 text-lg">
            Your order for <span className="text-white font-semibold">{product.name}</span> is confirmed.
          </p>
        </div>

        {/* Access Card */}
        <div className="glass-card rounded-xl p-8 mb-8">
          <h2 className="text-xl font-semibold mb-6">Access Your Course</h2>
          
          <p className="text-white/70 mb-6">
            Your course access is being created. Check your email for login instructions.
          </p>
          
          <a
            href={product.accessUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="premium-button inline-flex items-center gap-3 text-[#0F1115] px-8 py-4 font-semibold rounded-lg"
            data-testid="access-course-button"
          >
            <Play size={20} />
            Access Your Course
            <ExternalLink size={16} />
          </a>
          
          <p className="text-sm text-white/50 mt-4">
            Login instructions have been sent to your email.
          </p>
        </div>

        {/* Next Steps */}
        <div className="glass-card rounded-xl p-8 mb-8">
          <h2 className="text-xl font-semibold mb-6">What Happens Next</h2>
          <ul className="space-y-4">
            {product.nextSteps.map((step, index) => (
              <li key={index} className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-[#E5C77A]/20 flex items-center justify-center shrink-0">
                  <span className="text-[#E5C77A] text-sm font-semibold">{index + 1}</span>
                </div>
                <span className="text-white/80 pt-1">{step}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* VIP Calendar Booking */}
        {product.type === 'vip_course' && product.calendlyUrl && (
          <div className="glass-card rounded-xl p-8 mb-8 border-[#E5C77A]/30">
            <div className="flex items-center gap-3 mb-4">
              <Calendar size={24} className="text-[#E5C77A]" />
              <h2 className="text-xl font-semibold">Schedule Your Strategy Call</h2>
            </div>
            <p className="text-white/70 mb-6">
              As a VIP member, you have a 90-minute 1-on-1 strategy call with Ramon. 
              Click the button below to select a time that works for you.
            </p>
            <a
              href={product.calendlyUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="secondary-button inline-flex items-center gap-2 text-[#E5C77A] px-6 py-3 rounded-lg"
            >
              <Calendar size={18} />
              Book Your Strategy Call
              <ExternalLink size={16} />
            </a>
          </div>
        )}

        {/* IP Intensive - VIP Only */}
        {product.type === 'vip_course' && (
          <div className="rounded-2xl overflow-hidden mb-8 border border-[#E5C77A]/30">
            {/* IP Intensive Header */}
            <div className="bg-gradient-to-b from-[#C6A85E]/[0.12] to-transparent p-10 text-center">
              <p className="uppercase tracking-[0.3em] text-[#E5C77A]/80 text-sm mb-4">Exclusive VIP Opportunity</p>
              <h2 className="serif-heading text-2xl md:text-3xl font-normal mb-3">
                You've Just Joined the Top 1% of IP Creators.
              </h2>
              <p className="text-white/60 text-lg">Most people stop here.</p>
            </div>

            <div className="px-8 md:px-10 pb-10 space-y-8">
              <div className="space-y-4 text-white/80 leading-relaxed">
                <p>
                  You didn't get to VIP by accident. You're serious about your story — serious enough to invest in the knowledge, the frameworks, and the access that most creators never find.
                </p>
                <p>So let's talk about what's next.</p>
              </div>

              {/* Introducing */}
              <div className="pt-4">
                <h3 className="serif-heading text-xl md:text-2xl font-normal text-[#E5C77A] mb-2">
                  Introducing: The IP Intensive
                </h3>
                <p className="text-lg text-white/90 font-semibold mb-1">A Private Full-Day Workshop With Ramon Govea</p>
                <p className="text-[#E5C77A]/80 text-sm font-semibold mb-6">Only 10 Spots Available Worldwide Per Year</p>

                <div className="space-y-4 text-white/80 leading-relaxed">
                  <p>
                    This isn't a webinar. It isn't a group call.
                  </p>
                  <p>
                    This is one full day — just you and Ramon — dedicated entirely to developing your IP.
                    We'll spend 6 focused hours with a working lunch, breaking down your story, building your world, 
                    developing your characters, mapping your pitch materials, and designing the DTC strategy that gets 
                    your project in front of buyers before you ever need a studio behind you.
                  </p>
                  <p className="text-white font-medium">
                    By the time the day ends, you won't just have a better idea. You'll have a project that's ready to move.
                  </p>
                </div>
              </div>

              {/* What You Walk Away With */}
              <div className="glass-card rounded-xl p-8">
                <h4 className="text-lg font-semibold text-white mb-5">What You Walk Away With:</h4>
                <div className="space-y-3">
                  {[
                    "A battle-tested pitch outline structured for Hollywood buyers",
                    "A complete IP Bible — world, characters, tone, franchise potential",
                    "A DTC social marketing strategy built around your specific project",
                    "Recorded session notes — every decision, every direction, documented",
                    "An official LinkedIn recommendation from Ramon Govea, personally written and posted — a credential that grows in value as his career does"
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <span className="text-[#E5C77A] mt-0.5">&#10022;</span>
                      <span className="text-white/80">{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* What's Included */}
              <div className="glass-card rounded-xl p-8">
                <h4 className="text-lg font-semibold text-white mb-5">What's Included:</h4>
                <div className="space-y-3">
                  {[
                    "6-hour private IP development session with Ramon",
                    "2 meals together (breakfast and lunch — the real conversations happen over food)",
                    "Accommodations for one night coordinated around Ramon's schedule in Miami or Los Angeles",
                    "Flight not included"
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <span className="text-[#E5C77A] mt-0.5">&#10022;</span>
                      <span className="text-white/80">{item}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-6 pt-6 border-t border-white/10">
                  <p className="text-white/50 text-sm">Investment:</p>
                  <p className="text-3xl font-bold text-[#E5C77A]">$7,500</p>
                </div>
              </div>

              {/* This Is Not For Everyone */}
              <div className="space-y-4 text-white/80 leading-relaxed">
                <h4 className="text-lg font-semibold text-white">This Is Not For Everyone.</h4>
                <p>
                  Ten spots. One year. That's it.
                </p>
                <p>
                  This is for the creator who has a story they believe in deeply enough to bet a day on it. Who wants not just feedback — but a co-pilot with 15 years of Hollywood development experience, $2.6M raised, and 10 IPs sold sitting across the table from them.
                </p>
                <p className="text-white font-medium">If that's you, we want to hear from you.</p>
              </div>

              {/* How To Apply */}
              <div className="rounded-xl p-8 bg-gradient-to-br from-[#1a2035] to-[#0f1420] border-l-4 border-[#E5C77A]">
                <h4 className="text-lg font-semibold text-white mb-4">How To Apply:</h4>
                <div className="space-y-4 text-white/80 leading-relaxed">
                  <p>
                    This isn't a checkout button. It's an expression of interest — because the right fit matters as much as the investment. Ten spots means only a few candidates get the opportunity per year.
                  </p>
                  <p>
                    Email Ramon directly at{' '}
                    <a href="mailto:ramon@mythdivision.com" className="text-[#E5C77A] hover:underline font-semibold">
                      ramon@mythdivision.com
                    </a>
                    {' '}with the subject line:
                  </p>
                  <div className="bg-black/30 rounded-lg px-6 py-4 text-center">
                    <p className="text-white font-semibold text-lg">
                      IP INTENSIVE — [Your Name / Your Project Title]
                    </p>
                  </div>
                  <p>
                    Tell him who you are, what your IP is, and where you are in development. Keep it tight — if your pitch can't fit in an email, we've got work to do.
                  </p>
                  <p className="text-white font-medium">He reads every one personally.</p>
                  <p className="text-white/60 text-sm mt-4">
                    Spots are filled on a first-come, first-served basis among qualified applicants. Once 10 spots are confirmed for the year, the waitlist opens for the following year.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Upsell */}
        {product.upsell && (
          <div className="border border-[#E5C77A]/30 rounded-xl p-8 text-center bg-gradient-to-b from-[#C6A85E]/[0.06] to-transparent">
            <p className="text-[#E5C77A] uppercase tracking-widest text-sm mb-2">{product.upsell.title}</p>
            {product.upsell.scarcity && (
              <p className="text-white/90 text-sm font-semibold mb-3 bg-[#E5C77A]/10 inline-block px-4 py-1.5 rounded-full">
                {product.upsell.scarcity}
              </p>
            )}
            <p className="text-white/70 mb-6">{product.upsell.description}</p>
            <button
              onClick={() => navigate(`/ai-tools-for-creatives/checkout/${product.upsell.product}`)}
              className="premium-button inline-flex items-center gap-2 text-[#0F1115] px-8 py-4 rounded-lg font-semibold"
              data-testid="upsell-button"
            >
              {product.upsell.ctaText} — ${product.upsell.price}
              <ArrowRight size={18} />
            </button>
          </div>
        )}

        {/* Community */}
        <div className="text-center mt-12 pt-8 border-t border-white/10">
          <h3 className="text-lg font-semibold mb-3">Join the Community</h3>
          <p className="text-white/60 mb-6">
            Connect with other creators in our private Skool community. 
            Check your welcome email for the invite link.
          </p>
          
          <div className="flex flex-wrap justify-center gap-6 text-sm text-white/50">
            <span className="flex items-center gap-2">
              <Shield size={14} className="text-[#E5C77A]" />
              Lifetime Access
            </span>
            <span className="flex items-center gap-2">
              <Shield size={14} className="text-[#E5C77A]" />
              30-Day Guarantee
            </span>
            <span className="flex items-center gap-2">
              <Shield size={14} className="text-[#E5C77A]" />
              Free Updates
            </span>
          </div>
        </div>

        {/* Support Footer */}
        <div className="text-center mt-12">
          <p className="text-white/40 mb-2">Questions?</p>
          <a href="mailto:support@ramongovea.com" className="text-[#E5C77A] hover:underline flex items-center justify-center gap-2">
            <Mail size={18} />
            support@ramongovea.com
          </a>
        </div>
      </div>
    </div>
  );
};

export default AIToolsSuccess;
